import React, { useState } from 'react'
import styled from 'styled-components'
import DownloadIcon from '../../assets/icons/DownloadIcon';
import { useTheme } from '../../middleware/ThemeContext';
import { font, Theme } from '../../utils/themes';
import StyledText from '../../components/StyledComponents/StyledText';
import { useDownloadFileMutation } from '../../api/contactApiSlice';
import { useToast } from '../../components/toast/ToastContext';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { SkeletonCell } from '../../components/SkeletonCell';
import { getStatusLabel, statusColorMapping } from '../../utils/stausMapping';
import { capitalize } from 'lodash';
import { getPercentage } from '../../utils/helper';
import { FlagBadge } from '../../components/listing/DataTable';


interface FileHeaderProps {
  data: Record<string, string | number | undefined>;
  labels?: Record<string, string>;
  fileId?: string;
  loading?: boolean;
  totalRecords?: number;
  totalProcessed?: number;
}
const FileHeader: React.FC<FileHeaderProps> = ({ data, labels, fileId, loading, totalRecords, totalProcessed }) => {
  const { theme } = useTheme();
  const [download, { isLoading }] = useDownloadFileMutation();
  const showToast = useToast();
  const [loadingKey, setLoadingKey] = useState<string | null>(null);

  function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
    return typeof error === 'object' && error != null && 'data' in error;
  }

  function isSerializedError(error: unknown): error is SerializedError {
    return typeof error === 'object' && error != null && 'message' in error;
  }
  const handleDownload = async (fileType: string) => {
    let transformFileType = fileType === "errorCounts" ? "errorFile" : fileType === "duplicates" ? "duplicateFile" : fileType === "fileName" && "originalFile";
    setLoadingKey(fileType);
    try {
      await download({ id: fileId, fileType: transformFileType }).then((res) => {

        if (isFetchBaseQueryError(res.error)) {
          // Now TypeScript knows it's FetchBaseQueryError
          const message =
            typeof res.error.data === 'object' &&
              res.error.data !== null &&
              'message' in res.error.data
              ? String(res.error.data.message)
              : 'Unknown error';
          showToast('Error', message, 'error');
        } else if (isSerializedError(res.error)) {
          showToast('Error', res.error.message || 'Unknown error', 'error');
        }
        else {
          const link = document.createElement("a");
          link.href = res?.data?.response;

          // Optional: set a default filename
          link.download = fileType;

          // Append to body, trigger click, then remove
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

      })



    } catch (error) {
      console.error("Error downloading file:", error);
    }
    finally {
      setLoadingKey(null);
    }
  }

  const displayBadge = (statusID: number) => {
    const label = getStatusLabel(Number(statusID));
    const circleColor = statusColorMapping[label.toUpperCase()] || statusColorMapping.DEFAULT;
    return (
      <FlagBadge $circleColor={circleColor}>
        {capitalize(label)}
        {totalRecords != null && totalProcessed != null && capitalize(label) === "Processing" && (
          <span>
            ({getPercentage(totalRecords, totalProcessed)}%)
          </span>
        )}
      </FlagBadge>
    );
  }

  return (
    <SummaryContainer theme={theme}>

      {
        Object.keys(data).map((key) => (
          <Item key={key}>
            <StyledText textColor={theme.CorporateTheme.colors.textPrimary} fontSize='16px' fontWeight={600} fontFamily={font.raleway}>
              {labels?.[key] || key}{" "}
              {((key === "errorCounts" || key === "duplicates") && Number(data?.[key] ?? 0) > 0) || key === "fileName" ? (
                loadingKey === key ? (
                  <LoadingSpinner size="16px" color={theme.CorporateTheme.colors.textPrimary} />
                ) : (
                  <span className="cursor-pointer" onClick={() => handleDownload(key)}>
                    <DownloadIcon />
                  </span>
                )
              ) : null}

            </StyledText>
            {/* <StyledText className='mt-2' fontSize='14px' fontWeight={500} fontFamily={font.lato} textColor={theme.CorporateTheme.colors.textPrimary}>{data[key]}</StyledText> */}
            {loading ? (
              <SkeletonCell width="100px" height="16px" className="mt-2" />
            ) : (
              <StyledText
                className="mt-2"
                fontSize="14px"
                fontWeight={500}
                fontFamily={font.lato}
                textColor={theme.CorporateTheme.colors.textPrimary}
              >
                {
                  key === "status" ? (
                    <>
                      {/* {capitalize(getStatusLabel(Number(data[key])))}
                      {totalRecords != null && totalProcessed != null && (
                        <span style={{ opacity: 0.7, marginLeft: '4px' }}>
                          ({getPercentage(totalRecords, totalProcessed)}%)
                        </span>
                      )} */}
                      {displayBadge(Number(data[key]))}
                    </>
                  ) : (
                    data[key]
                  )
                }
              </StyledText>
            )}
          </Item>

        ))}
    </SummaryContainer>
  );
};

const SummaryContainer = styled.div<{ theme: Theme }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 40px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background-color: ${({ theme }) => theme?.CorporateTheme?.colors?.background};
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
  @media (max-width: 1075px) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    justify-content: center;
  }
`;


const Item = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 14px;
`;

const Label = styled.span`
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const Value = styled.span`
  color: #555;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export default FileHeader
